import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from 'contexts/AuthContext';
import { AwardRoleProvider } from 'contexts/AwardRoleContext';
import AwardHome from 'containers/SMDDAwardHome';
import SMDDFormPage from 'containers/SMDDFormPage';
import PreselectionRankingPage from 'containers/PreselectionRankingPage';
import QualificationFormPage from 'containers/QualificationFormPage';
import QualificationEvaluatorPage from 'containers/QualificationEvaluatorPage';
import QualificationRankingPage from 'containers/QualificationRankingPage';
import FinalRankingPage from 'containers/FinalRankingPage';
import FinalistListPage from 'containers/FinalistListPage';
import FinalEvaluatorPage from 'containers/FinalEvaluatorPage';
import Login from 'pages/Login';
import OAuthCallback from 'pages/OAuthCallback';
import WinnerDetail from 'containers/WinnerDetail/WinnerDetail';
import CategoryMappingPage from 'containers/CategoryMappingPage';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

// Toggle this flag to enable/disable authentication for protected routes
// Set to false to disable auth temporarily, true to enable auth
const REQUIRE_AUTH = true;

interface RouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<RouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated() ? (
    (children as React.ReactElement)
  ) : (
    <Navigate to="/login" />
  );
};

// Wrapper that conditionally applies authentication based on REQUIRE_AUTH flag
const ConditionalPrivateRoute: React.FC<RouteProps> = ({ children }) => {
  if (!REQUIRE_AUTH) {
    return children as React.ReactElement; // Skip authentication when flag is false
  }
  return <PrivateRoute>{children}</PrivateRoute>;
};

// Helper component to control ReCAPTCHA badge visibility based on the active route
const RecaptchaVisibilityController: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const showPaths = ['/smdd-form', '/smdd-form/admin', '/form'];
    // Normalize path by removing trailing slash
    const normalizedPath = location.pathname.replace(/\/$/, '');
    if (showPaths.includes(normalizedPath)) {
      document.body.classList.add('show-recaptcha');
    } else {
      document.body.classList.remove('show-recaptcha');
    }
  }, [location.pathname]);

  return null;
};

function App() {
  // React Router requires the basename to NOT have a trailing slash
  const baseUrl = import.meta.env.BASE_URL || '/';
  const basename = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

  const content = (
    <AuthProvider>
      <AwardRoleProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/oauth2/callback" element={<OAuthCallback />} />
          <Route path="/" element={<AwardHome />} />
          <Route path="/awards" element={<Navigate to="/" replace />} />

          {/* Pre-Selection */}
          <Route path="/smdd-form" element={<SMDDFormPage />} />
          <Route
            path="/smdd-form/admin"
            element={
              <ConditionalPrivateRoute>
                <SMDDFormPage isAdmin={true} />
              </ConditionalPrivateRoute>
            }
          />
          <Route
            path="/smdd-submissions"
            element={<PreselectionRankingPage />}
          />

          {/* Qualification */}
          <Route
            path="/form"
            element={
              <ConditionalPrivateRoute>
                <QualificationFormPage />
              </ConditionalPrivateRoute>
            }
          />
          <Route
            path="/evaluation"
            element={
              <ConditionalPrivateRoute>
                <QualificationEvaluatorPage />
              </ConditionalPrivateRoute>
            }
          />
          <Route
            path="/submissions"
            element={
              <ConditionalPrivateRoute>
                <QualificationRankingPage />
              </ConditionalPrivateRoute>
            }
          />
          <Route
            path="/finalists"
            element={
              <ConditionalPrivateRoute>
                <FinalistListPage />
              </ConditionalPrivateRoute>
            }
          />

          {/* Final */}
          <Route
            path="/final-evaluation"
            element={
              <ConditionalPrivateRoute>
                <FinalEvaluatorPage />
              </ConditionalPrivateRoute>
            }
          />
          <Route
            path="/final-submissions"
            element={
              <ConditionalPrivateRoute>
                <FinalRankingPage />
              </ConditionalPrivateRoute>
            }
          />

          {/* Other Page */}
          <Route
            path="/category-mapping"
            element={
              <ConditionalPrivateRoute>
                <CategoryMappingPage />
              </ConditionalPrivateRoute>
            }
          />
          <Route
            path="/winners/:id"
            element={
              <ConditionalPrivateRoute>
                <WinnerDetail />
              </ConditionalPrivateRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AwardRoleProvider>
    </AuthProvider>
  );

  return (
    <Router basename={basename}>
      <RecaptchaVisibilityController />
      {import.meta.env.VITE_DISABLE_RECAPTCHA === 'true' ? (
        content
      ) : (
        <GoogleReCaptchaProvider
          reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''}
        >
          {content}
        </GoogleReCaptchaProvider>
      )}
    </Router>
  );
}

export default App;
